import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../../contexts/AppContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import type { Page } from '../../../types';
import { notify } from '../../../utils/toast';
import {
    fetchOrganizationSubscription,
    syncOrganizationBilling,
} from '../../../services/modules/subscriptionService';
import { useSubscriptionLock } from './useSubscriptionLock';
import { isMetaOAuthFlowActive } from '../../onboarding/metaReturn';
import { markPendingOnboarding } from '../../onboarding/pendingOnboarding';
import { isPartnerPage } from '../../../utils/routeUtils';

/** Páginas de faturamento/planos — apenas owner/admin */
const BILLING_PAGES: Page[] = ['billing', 'subscription'];

export const CHECKOUT_SESSION_KEY = 'adlitics_checkout_session';
const CHECKOUT_SYNC_LOCK = 'adlitics_checkout_syncing';

const hasPendingCheckout = () =>
    !!sessionStorage.getItem(CHECKOUT_SYNC_LOCK) || !!sessionStorage.getItem(CHECKOUT_SESSION_KEY);

const clearCheckoutSession = () => {
    sessionStorage.removeItem(CHECKOUT_SESSION_KEY);
    sessionStorage.removeItem(CHECKOUT_SYNC_LOCK);
};

const replacePath = (path: string) => {
    if (window.location.pathname !== path) {
        window.history.replaceState(null, '', path);
    }
};

/** Remove query string mantendo o pathname atual. */
const stripUrlQuery = () => {
    window.history.replaceState(null, '', window.location.pathname);
};

async function pollBillingSync(options: {
    maxAttempts: number;
    delayMs: number;
    shouldStop: () => boolean;
    sync: () => Promise<{ synced: boolean }>;
    onError: (err: unknown) => void;
}): Promise<boolean> {
    for (let attempt = 1; attempt <= options.maxAttempts && !options.shouldStop(); attempt++) {
        if (attempt > 1) await new Promise((r) => setTimeout(r, options.delayMs));
        try {
            const result = await options.sync();
            if (result.synced) return true;
        } catch (err) {
            options.onError(err);
        }
    }
    return false;
}

export interface CheckoutBillingSyncState {
    isCheckoutProcessing: boolean;
    needsPlanSelection: boolean;
    effectiveNeedsPlan: boolean;
    isSubscriptionBlocked: boolean;
}

/**
 * Orquestra retorno do Stripe checkout, heal de billing e redirects de plano.
 * Mantém o layout livre da máquina de estado de sessionStorage.
 */
export function useCheckoutBillingSync(): CheckoutBillingSyncState {
    const { currentPage, setCurrentPage } = useAppContext();
    const { currentOrganization, refreshOrganizations } = useOrganization();
    const { canViewBilling } = usePermissions();
    const { isBlocked: isSubscriptionBlocked, status: subscriptionStatus } = useSubscriptionLock();

    const needsPlanSelection = subscriptionStatus === 'no_plan';
    const [isCheckoutProcessing, setIsCheckoutProcessing] = useState(hasPendingCheckout);
    const effectiveNeedsPlan = needsPlanSelection && !isCheckoutProcessing;

    const currentOrgIdRef = useRef<string | null>(null);
    const healAttemptedRef = useRef(false);

    useEffect(() => {
        currentOrgIdRef.current = currentOrganization?.id ?? null;
    }, [currentOrganization?.id]);

    // Member/viewer não acessa faturamento
    useEffect(() => {
        if (canViewBilling || !BILLING_PAGES.includes(currentPage)) return;
        setCurrentPage('profile');
    }, [canViewBilling, currentPage, setCurrentPage]);

    // Sem plano → força /subscription (exceto durante checkout, Meta OAuth ou portal parceiro)
    useEffect(() => {
        if (isMetaOAuthFlowActive()) return;
        if (isPartnerPage(currentPage)) return;
        if (!needsPlanSelection || isCheckoutProcessing || currentPage === 'subscription') return;
        setCurrentPage('subscription');
        replacePath('/subscription');
    }, [needsPlanSelection, isCheckoutProcessing, currentPage, setCurrentPage]);

    // Durante checkout → dashboard (overlay cobre a UI)
    useEffect(() => {
        if (!isCheckoutProcessing || isMetaOAuthFlowActive()) return;
        if (currentPage !== 'dashboard') {
            setCurrentPage('dashboard');
        }
        replacePath('/dashboard');
    }, [isCheckoutProcessing, currentPage, setCurrentPage]);

    // Plano liberado → limpa sessão, avisa e dispara gate de onboarding
    useEffect(() => {
        if (!isCheckoutProcessing || needsPlanSelection) return;

        clearCheckoutSession();
        markPendingOnboarding();
        setIsCheckoutProcessing(false);
        notify.success('Plano ativado com sucesso!');
        // crm:billing-updated também força onboarding (OnboardingProvider)
        window.dispatchEvent(new CustomEvent('crm:billing-updated'));
    }, [isCheckoutProcessing, needsPlanSelection]);

    // Sync automático pós-checkout Stripe
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.get('canceled') === 'true') {
            notify.info('Checkout cancelado. Nenhuma cobrança foi realizada.');
            clearCheckoutSession();
            setIsCheckoutProcessing(false);
            stripUrlQuery();
            return;
        }

        const urlSessionId = params.get('session_id');
        if (params.get('success') === 'true' && urlSessionId) {
            sessionStorage.setItem(CHECKOUT_SESSION_KEY, urlSessionId);
        }

        const sessionId = urlSessionId || sessionStorage.getItem(CHECKOUT_SESSION_KEY);
        const isCheckoutReturn = params.get('success') === 'true' || !!sessionId;

        if (!isCheckoutReturn) return;
        if (!sessionId && !canViewBilling) return;
        if (sessionStorage.getItem(CHECKOUT_SYNC_LOCK)) return;

        sessionStorage.setItem(CHECKOUT_SYNC_LOCK, '1');
        setIsCheckoutProcessing(true);
        stripUrlQuery();

        let stopped = false;

        (async () => {
            const synced = await pollBillingSync({
                maxAttempts: 12,
                delayMs: 1500,
                shouldStop: () => stopped,
                sync: () =>
                    syncOrganizationBilling(
                        currentOrgIdRef.current ?? undefined,
                        sessionId ?? undefined,
                    ),
                onError: (err) => console.warn('[useCheckoutBillingSync] checkout sync error:', err),
            });

            if (synced) {
                await refreshOrganizations();
                return;
            }

            sessionStorage.removeItem(CHECKOUT_SYNC_LOCK);
            setIsCheckoutProcessing(false);
            if (!stopped) {
                notify.warning('Aguarde alguns instantes enquanto confirmamos sua assinatura...');
            }
        })();

        return () => {
            stopped = true;
        };
    }, [canViewBilling, refreshOrganizations]);

    // Fallback: customer Stripe existe sem subscription_id (webhook atrasado / perdido).
    // Inclui trial local — antes só rodava em no_plan e orgs em trialing ficavam presas.
    useEffect(() => {
        if (!canViewBilling || !currentOrganization?.id) return;
        if (sessionStorage.getItem(CHECKOUT_SESSION_KEY)) return;
        if (sessionStorage.getItem(CHECKOUT_SYNC_LOCK)) return;
        if (healAttemptedRef.current) return;

        let stopped = false;
        const organizationId = currentOrganization.id;

        (async () => {
            const org = await fetchOrganizationSubscription(organizationId);
            if (!org?.stripeCustomerId || org.stripeSubscriptionId) return;

            healAttemptedRef.current = true;
            console.info(
                '[useCheckoutBillingSync] healing unpaid Stripe gap',
                { organizationId, status: org.status },
            );

            const synced = await pollBillingSync({
                maxAttempts: 6,
                delayMs: 2000,
                shouldStop: () => stopped,
                sync: () => syncOrganizationBilling(organizationId),
                onError: (err) => console.warn('[useCheckoutBillingSync] billing heal error:', err),
            });

            if (!synced || stopped) return;

            await refreshOrganizations();
            window.dispatchEvent(new CustomEvent('crm:billing-updated'));
            notify.success('Assinatura confirmada com sucesso!');
        })();

        return () => {
            stopped = true;
        };
    }, [canViewBilling, currentOrganization?.id, refreshOrganizations]);

    return {
        isCheckoutProcessing,
        needsPlanSelection,
        effectiveNeedsPlan,
        isSubscriptionBlocked,
    };
}
